import React, { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import * as Yup from "yup";
import { useFormik } from "formik";
import classNames from "classnames";
import appUrl from "../../../config/appUrl";
import { toast } from "react-toastify";

const AddEditSite = ({ onHide, getSiteList, addEditSite, editData, siteDetail }) => {
  const [disable, setDisable] = useState(false);
  const [priceConfigurations, setPriceConfigurations] = useState([]); // all added configs
  const [showAvailablePrice, setShowAvailablePrice] = useState(false);
  const [assignedPrices, setAssignedPrices] = useState([]); // right list
  const [priceList, setPriceList] = useState([]); // left list
  const [loading, setLoading] = useState(true);
  const [duplicateError, setDuplicateError] = useState(false);
  const [configName, setConfigName] = useState(""); // current config name
  const [selectedAvailable, setSelectedAvailable] = useState([]);
  const [selectedAssigned, setSelectedAssigned] = useState([]);

  const isEditMode = addEditSite === true;
  const isViewMode = siteDetail === true;
  const createdBy = "65a11ecab59a0916363d09a3";

  // ✅ Validation Schema
  const validationSchema = Yup.object().shape({
    siteName: Yup.string().required("Site name is required."),
    location: Yup.string().required("Location is required."),
  });

  // ✅ Formik setup
  const formik = useFormik({
    validationSchema,
    initialValues: {
      siteName: "",
      location: "",
    },
    onSubmit: async (values) => {
      setDisable(true);

      const payload = isEditMode
        ? {
            id: editData?._id,
            siteName: values.siteName,
            location: values.location,
            priceConfigurations: priceConfigurations.map((p) => ({
              name: p.configName,
              site: editData?._id,
              priceSetupId: p.priceSetupId,
              createdBy,
            })),
          }
        : {
            siteName: values.siteName,
            location: values.location,
            priceConfigurations: priceConfigurations.map((p) => ({
              name: p.configName,
              priceSetupId: p.priceSetupId,
              createdBy,
            })),
          };

      try {
        const url = isEditMode
          ? `${appUrl.BASE_URL}Site/update`
          : `${appUrl.BASE_URL}Site/add`;

        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          await getSiteList();
          toast.success(isEditMode ? "Site updated successfully!" : "Site added successfully!");
          onHide();
        } else {
          toast.error("Something went wrong!");
        }
      } catch (error) {
        console.error(error);
        toast.error("Error while saving site.");
      } finally {
        setDisable(false);
      }
    },
  });

  // ✅ Edit Mode Setup
  useEffect(() => {
    if (isEditMode && editData) {
      formik.setValues({
        siteName: editData?.siteName || "",
        location: editData?.location || "",
      });

     const assigned =
  editData?.priceConfigurations?.map((p) => {
    const matched = editData?.availablePrices?.find((ap) => ap.id === p.priceSetupId);
    return {
      configName: p.name, // Configuration name (e.g. "hamza")
      priceSetupId: p.priceSetupId,
      name: matched ? matched.name : "No Price Found", // Price name (e.g. "Fixed Price Bike")
      id: p.priceSetupId,
      vehicleType: matched ? matched.vehicleType : "",
    };
  }) || [];


      setPriceConfigurations(assigned);

      // Left list = availablePrices that are not assigned
      const available = (editData?.availablePrices || []).filter(
        (ap) => !assigned.some((as) => as.priceSetupId === ap.id)
      );

      setPriceList(available);
      setLoading(false);
    }
  }, [isEditMode, editData]);

  // ✅ Add Mode - Load all available prices
  useEffect(() => {
    if (!isEditMode) {
      const getPriceList = async () => {
        try {
          const response = await fetch(`${appUrl.BASE_URL}PriceSetup/all`);
          const data = await response.json();
          setPriceList(data?.data || []);
          setLoading(false);
        } catch (error) {
          console.error(error);
        }
      };
      getPriceList();
    }
  }, [isEditMode]);

  const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
  const getFormErrorMessage = (name) =>
    isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;

  // ✅ Reset selections when dialog changes
  useEffect(() => {
    if (!showAvailablePrice) {
      setSelectedAvailable([]);
      setSelectedAssigned([]);
      setDuplicateError(false);
    }
  }, [editData, addEditSite]);

  // ✅ Move selected items right
 const moveRight = () => {
  if (selectedAvailable.length === 0) return;

  // 🔍 Check for duplicate vehicleType
  const duplicateType = selectedAvailable.find((item) =>
    assignedPrices.some(
      (assigned) =>
        assigned.vehicleType?.trim()?.toLowerCase() ===
        item.vehicleType?.trim()?.toLowerCase()
    )
  );

  if (duplicateType) {
    toast.error(
      `Multiple Vehicle Types Error: "${duplicateType.vehicleType}" is already assigned.`
    );
    return; // Stop further processing
  }

  // ✅ Add only if no duplicate vehicleType
  const filteredToAdd = selectedAvailable.filter(
    (item) => !assignedPrices.some((a) => a.id === item.id)  
  );

  setAssignedPrices([...assignedPrices, ...filteredToAdd]);
  setPriceList(priceList.filter((item) => !filteredToAdd.some((f) => f.id === item.id)));
  setSelectedAvailable([]);
};


  // ✅ Move selected items left
  const moveLeft = () => {
    const filteredBack = selectedAssigned.filter(
      (item) => !priceList.some((p) => p.id === item.id)
    );
    setPriceList([...priceList, ...filteredBack]);
    setAssignedPrices(
      assignedPrices.filter((a) => !selectedAssigned.some((s) => s.id === a.id))
    );
    setSelectedAssigned([]);
  };
  useEffect(() => {
    if (siteDetail && editData) {
      formik.setValues({
        siteName: editData?.siteName || "",
        location: editData?.location || "",
      });
  
      // If you also want price configs to show in view mode:
      const assigned =
        editData?.priceConfigurations?.map((p) => {
          const matched = editData?.availablePrices?.find(
            (ap) => ap.id === p.priceSetupId
          );
          return {
            configName: p.name,
            priceSetupId: p.priceSetupId,
            name: matched ? matched.name : "No Price Found",
            id: p.priceSetupId,
            vehicleType: matched ? matched.vehicleType : "",
          };
        }) || [];
  
      setPriceConfigurations(assigned);
      setPriceList(editData?.availablePrices || []);
    }
  }, [siteDetail, editData]);
  
  const moveItemUp = (index) => {
    if (index === 0) return; // Already at top
    const newAssigned = [...assignedPrices];
    [newAssigned[index - 1], newAssigned[index]] = [newAssigned[index], newAssigned[index - 1]];
    setAssignedPrices(newAssigned);
  };
  
  const moveItemDown = (index) => {
    if (index === assignedPrices.length - 1) return; // Already at bottom
    const newAssigned = [...assignedPrices];
    [newAssigned[index], newAssigned[index + 1]] = [newAssigned[index + 1], newAssigned[index]];
    setAssignedPrices(newAssigned);
  };
  // ✅ Add/Update configuration
  const handleAddUpdateConfig = () => {
    if (!configName.trim()) {
      toast.error("Please enter a configuration name");
      return;
    }

    const newConfigItems = assignedPrices.map((item) => ({
      configName,
      priceSetupId: item.id,
      name: item.name,
      vehicleType: item.vehicleType,
    }));

    setPriceConfigurations((prev) => {
      // Remove existing entries of same config name
      const filteredOld = prev.filter((p) => p.configName !== configName);
      return [...filteredOld, ...newConfigItems];
    });

    setShowAvailablePrice(false);
    setConfigName("");
    setAssignedPrices([]);
    setSelectedAvailable([]);
    setSelectedAssigned([]);
    
  };

  return (
    <>
     <h5>
  {isViewMode ? "View Site" : isEditMode ? "Edit Site" : "Add Site"}
</h5>

      <div className="brdr__top">
        <form onSubmit={formik.handleSubmit}>
          <div className="card">
            <div className="p-fluid formgrid grid">
              {/* Site Name */}
              <div className="field col-12 md:col-6">
                <label style={{ fontWeight: "bold" }}>Site Name</label>
                <span style={{ color: "red" }}>*</span>
                <InputText
                  id="siteName"
                  name="siteName"
                  value={formik.values.siteName}
                  onChange={(e) => formik.setFieldValue("siteName", e.target.value.slice(0, 250))}
                  className={classNames(
                    { "p-invalid": isFormFieldValid("siteName") },
                    "TextArea__Round"
                  )}
                  placeholder="Enter Site Name"
                  disabled={isEditMode || isViewMode}
                />
                {getFormErrorMessage("siteName")}
              </div>

              {/* Location */}
              <div className="field col-12 md:col-6">
                <label style={{ fontWeight: "bold" }}>Site Location</label>
                <span style={{ color: "red" }}>*</span>
                <InputText
                  id="location"
                  name="location"
                  value={formik.values.location}
                  onChange={(e) => formik.setFieldValue("location", e.target.value.slice(0, 250))}
                  className={classNames(
                    { "p-invalid": isFormFieldValid("location") },
                    "TextArea__Round"
                  )}
                  placeholder="Enter Site Location"
                  disabled={isEditMode || isViewMode}
                />
                {getFormErrorMessage("location")}
              </div>

              {/* Price Configuration Section */}
              {showAvailablePrice ? (
                <>
                  <div className="field col-6 md:col-6">
                    <label style={{ fontWeight: "bold" }}>Price Configuration Name</label>
                    <InputText
                      id="configName"
                      value={configName}
                      onChange={(e) => setConfigName(e.target.value)}
                      className="w-full"
                      placeholder="Enter Configuration Name"
                      disabled={isViewMode}
                    />
                  </div>

                  {/* Available + Assigned Prices */}
                  <div className="field col-12 md:col-12 mt-3">
                    <div className="p-fluid formgrid grid">
                      {/* Left (Available) */}
                      <div className="field col-5 md:col-5">
                        <label style={{ fontWeight: "bold" }}>Available Prices</label>
                        <div
                          style={{
                            border: "1px solid #D5D5D5",
                            height: "200px",
                            overflowY: "auto",
                            padding: "10px",
                          }}
                        >
                          {loading ? (
                            <p>Loading...</p>
                          ) : (
                            priceList.map((item) => (
                              <div
                                key={item.id}
                                style={{
                                  padding: "5px 10px",
                                  borderBottom: "1px solid #ddd",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedAvailable.some((p) => p.id === item.id)}
                                  onChange={(e) => {
                                    if (e.target.checked)
                                      setSelectedAvailable([...selectedAvailable, item]);
                                    else
                                      setSelectedAvailable(
                                        selectedAvailable.filter((p) => p.id !== item.id)
                                      );
                                  }}
                                />
                                <span>
                                  {item.name}
                                  {item.vehicleType && (
                                    <span style={{ color: "#666" }}>
                                      {" "}
                                      ({item.vehicleType})
                                    </span>
                                  )}
                                </span>
                                
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {/* Move Buttons */}
                      <div
                        className="field col-2 md:col-2 flex justify-content-center align-items-center"
                        style={{ display: "flex", flexDirection: "column", gap: "10px" }}
                      >
                        <Button
                            type="button" 
                          icon="pi pi-arrow-right"
                          className="p-button-rounded p-button-success"
                          onClick={moveRight}
                          disabled={selectedAvailable.length === 0}
                        />
                        <Button
                            type="button" 
                          icon="pi pi-arrow-left"
                          className="p-button-rounded p-button-danger"
                          onClick={moveLeft}
                          disabled={selectedAssigned.length === 0}
                        />
                      </div>

                     {/* Right (Assigned) */}
<div className="field col-5 md:col-5">
  <label style={{ fontWeight: "bold" }}>Assigned Prices</label>
  <div
    style={{
      border: "1px solid #D5D5D5",
      height: "200px",
      overflowY: "auto",
      padding: "10px",
    }}
  >
    {assignedPrices.length > 0 ? (
      assignedPrices.map((item, index) => (
        <div
          key={item.id}
          style={{
            padding: "5px 10px",
            borderBottom: "1px solid #ddd",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input
              type="checkbox"
              checked={selectedAssigned.some((p) => p.id === item.id)}
              onChange={(e) => {
                if (e.target.checked)
                  setSelectedAssigned([...selectedAssigned, item]);
                else
                  setSelectedAssigned(
                    selectedAssigned.filter((p) => p.id !== item.id)
                  );
              }}
            />
            <div>
              {item.name}
              {item.vehicleType && (
                <span style={{ color: "#666" }}> ({item.vehicleType})</span>
              )}
            </div>
          </div>

          {/* Up / Down Buttons */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Button
              icon="pi pi-arrow-up"
              className="p-button-text p-button-sm"
              onClick={() => moveItemUp(index)}
              disabled={index === 0}
            />
            <Button
              icon="pi pi-arrow-down"
              className="p-button-text p-button-sm"
              onClick={() => moveItemDown(index)}
              disabled={index === assignedPrices.length - 1}
            />
          </div>
        </div>
      ))
    ) : (
      <p>No prices added</p>
    )}
  </div>
</div>

                      {/* Bottom Buttons */}
                      <div className="col-12 text-right mt-3">
                        <Button
                          label="Cancel"
                          className="Cancel__button mr-3"
                          type="button"
                          onClick={() => setShowAvailablePrice(false)}
                        />
                        <Button
                          label="Add / Update"
                          className="Save__Button"
                          type="button"
                          onClick={handleAddUpdateConfig}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Configurations Table */}
                  <div className="field col-12 md:col-12 site-config-tabl ">
                    <table
                      className="p-datatable p-component mt-2"
                      style={{
                        width: "100%",
                        border: "1px solid #D5D5D5",
                        borderRadius: "8px",
                   
                      }}
                    >
                      <thead>
                        <tr>
                          <th style={{ width: "40%", textAlign: "left" }}>Price Configuration</th>
                          <th style={{ width: "40%", textAlign: "left" }}>Prices</th>
                          {!siteDetail && (<th style={{ width: "20%", textAlign: "left" }}>Action</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {priceConfigurations?.length > 0 ? (
                          Object.entries(
                            priceConfigurations.reduce((acc, cur) => {
                              acc[cur.configName] = acc[cur.configName] || [];
                              acc[cur.configName].push(cur);
                              return acc;
                            }, {})
                          ).map(([config, prices], idx) => (
                            <tr key={idx}>
                              <td>{config}</td>
                              <td>
                                {prices.map((p) => (
                                  <div key={p.priceSetupId}>
                                    {p.name}
                                    {p.vehicleType && (
                                      <span style={{ color: "#666" }}>
                                        {" "}
                                        ({p.vehicleType})
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </td>
                              {!siteDetail && (
                              <td>
                                <Button
                                  icon="pi pi-pencil"
                                  className="p-button-text p-button-sm"
                                  tooltip="Edit"
                                  onClick={() => {
                                    setConfigName(config);
                                    setAssignedPrices(prices);
                                    const remaining = (editData?.availablePrices || priceList).filter(
                                      (p) => !prices.some((a) => a.id === p.id)
                                    );
                                    setPriceList(remaining);
                                    setShowAvailablePrice(true);
                                  }}
                                />
                              </td>
                              )}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3" style={{ textAlign: "center" }}>
                              No Price Configurations added yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    <div className="col-12 text-right">
                      {!isViewMode && (
                        <Button
                          type="button"
                          disabled={!isEditMode} 
                          className="Save__Button mt-3"
                          label="New Site Config"
                          onClick={() => {
                            setShowAvailablePrice(true);
                            setAssignedPrices([]);
                            setSelectedAvailable([]);
                            setSelectedAssigned([]);
                            setConfigName("");
                          }}
                        />
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Bottom Buttons */}
            {!showAvailablePrice && (
              <div className="col-12 text-right">
                <Button
                  label={isViewMode ? "Close" : "Cancel"}
                  className="Cancel__button mr-3"
                  type="button"
                  onClick={onHide}
                />
                {!isViewMode && (
                  <Button
                    disabled={disable}
                    type="submit"
                    className="Save__Button"
                    label={isEditMode ? "Update" : "Save"}
                  />
                )}
              </div>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default AddEditSite;
